import { ComparisonPackageFeatures } from '../ComparisonTableTypes';
import { PACKAGES } from '../constants';

/**
 * We export a map of package key (ID/name) to the map of comparison
 * features that that package supports.
 */
export type SummaryComparisonPackages = {
  [packageKey: string]: ComparisonPackageFeatures;
};

// The team and enterprise plans include all the features of the plan
// below them, so we'll start with advantage and extend it up to include
// what we need for each package to be as non-redundant as possible
const TEAM_ADVTANTAGE: ComparisonPackageFeatures = {
  // Essentials
  unlimited_surveys: true,
  unlimited_questions: true,
  unlimited_responses: true,
  unlimited_collectors: true,

  // Support
  email_support: true,

  // Building Surveys
  essential_question_types: true,
  survey_templates: true,
  quizzes: true,
  question_page_skip_logic: true,
  question_page_randomization: true,
  a_b_test_question: true,
  custom_variables: true,
  file_upload: true,
  accept_payments: true,

  // Branding and Customization
  custom_logo_colors_url: true,
  custom_email_invites: true,

  // Response Collection
  recurring_surveys: true,
  require_answer_completion: true,
  survey_end_date: true,
  view_ip_addresses: true,
  confirmation_email: true,

  // Analysis
  data_trends: true,
  filter_and_compare: true,
  custom_reports: true,
  statistical_significance: true,
  data_exports: true,
  word_clouds: true,
  results_dashboards: true,
  industry_benchmarks: true,

  // Collaboration
  build_together: true,
  survey_sharing_with_editing: true,
  fine_permission_controls: true,
  team_analyze: true,
  shared_templates: true,
  add_reassign_accounts: true,

  // Integrations
  free_integrations: true,

  // Compliance and Governance
  password_protected: true,
  iso_27001: true,
  accessible: true,
};

const TEAM_PREMIER: ComparisonPackageFeatures = {
  // Include everything from team advantage, plus...
  ...TEAM_ADVTANTAGE,

  // Support
  phone_support: true,

  // Building surveys
  adv_branching_and_piping: true,
  block_randomization: true,
  multilingual_surveys: true,

  // Branding and Customization
  remove_footer: true,

  // Response Collection
  change_survey_url: true,
  website_redirect: true,

  // Analysis
  sentiment_analysis: true,
  crosstabs: true,

  // Integrations
  direct_api: true,
};

const ENTERPRISE: ComparisonPackageFeatures = {
  // Everything in team premier plus... all the other things
  ...TEAM_PREMIER,

  // Support
  customer_success_manager: true,

  // Branding and Customization
  custom_subdomain: true,
  custom_email: true, // Also in compliance section
  custom_terms: true, // Also in compliance section
  custom_question_bank: true,

  // Response Collection
  sms_offline_collection: true,
  smart_notifications: true,
  hippa_compliance: true,

  // Collaboration
  admin_dashboard: true,
  roles_workgroups: true,
  global_settings: true,

  // Integrations
  api_limits: true,
  bi_integrations: true,

  // Compliance and Governance
  activity_tracking: true,
  single_signon: true,
  account_control: true,
};

/**
 * This is the list of default options for the summary comparison table.
 * It can be altered/extended for experiments as needed.
 */
const SummaryComparisonPackageFeatures: SummaryComparisonPackages = {
  [PACKAGES.TeamAdvantage]: TEAM_ADVTANTAGE,
  [PACKAGES.TeamPremier]: TEAM_PREMIER,
  [PACKAGES.Enterprise]: ENTERPRISE,
};

export default SummaryComparisonPackageFeatures;
